import isPriceHidden from './isPriceHidden';
import getPriceBillingConfig from './getPriceBillingConfig';

/**
 * Prices a buyer can actually pick: enabled and not hidden. Hidden prices are
 * revealable via `?show_prices=<reference>` (see isPriceHidden).
 */
export function selectablePrices(prices = [], query = {}) {
  return (Array.isArray(prices) ? prices : []).filter(
    p => p.enabled !== false && !isPriceHidden(p, query),
  );
}

/**
 * A product is "multi-price" (shows the price picker) when it has 2+ enabled
 * prices. Hidden prices COUNT toward this — they're revealable via `show_prices`,
 * so a product with one visible + one hidden price still shows the picker (with
 * the visible option; the hidden one appears once revealed). At least one visible
 * option must remain for the picker to render.
 */
export function isMultiPrice(prices = [], query = {}) {
  const enabledCount = (Array.isArray(prices) ? prices : []).filter(
    p => p.enabled !== false,
  ).length;
  return enabledCount > 1 && selectablePrices(prices, query).length > 0;
}

/**
 * A single pay-what-you-want product: no price picker (not multi-price) and the
 * lone price (or a legacy charge-PWYW product with no prices[]) is PWYW. Such
 * products collect the amount via the PWYW input rather than showing a fixed
 * price line. Shared by Product (content render) and CheckoutDouble (form
 * render) so they agree on where the input belongs.
 */
export function isSinglePwyw(product, query = {}) {
  if (isMultiPrice(product?.prices, query)) return false;
  const onlyPrice = selectablePrices(product?.prices, query)[0];
  // TODO(M14): drop the legacy product.type/payWhatYouWant fallback once the prices[] backfill migration has run
  return onlyPrice
    ? !!onlyPrice.payWhatYouWant
    : product?.type === 'charge' && !!product?.payWhatYouWant;
}

/**
 * Whether the buyer's current purchase is recurring. Branches on the selected
 * price's billingType; without a resolved selectable price (legacy products
 * with no prices[], or a multi-price product before a selection) it falls
 * back to the legacy product-level type. One gate so Stripe Elements mode,
 * cost computation and the submit flow can't disagree — a one-time price
 * selected on a legacy 'subscription'-typed product must check out as a
 * payment.
 */
export function isRecurringPurchase(selectedPrice, product) {
  if (selectedPrice) return selectedPrice.billingType === 'recurring';
  // TODO(M14): legacy product.type fallback — clean up after the prices[] backfill migration
  return product?.type === 'subscription';
}

/**
 * Interval to display for the resolved selected price. Products with prices[]
 * are judged only by the selected price (null when it is one-time or nothing
 * is selected); legacy products fall back to the product-level interval.
 * Returns { interval, intervalCount } or null.
 */
export function getEffectiveInterval(selectedPrice, product) {
  if (product?.prices?.length > 0) {
    const config = getPriceBillingConfig(selectedPrice);
    if (selectedPrice?.billingType === 'recurring' && config?.interval) {
      return { interval: config.interval, intervalCount: config.intervalCount };
    }
    return null;
  }
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.interval/intervalCount)
  if (!product?.interval) return null;
  return { interval: product.interval, intervalCount: product.intervalCount };
}

/**
 * Resolve the buyer's effective selected price from the SELECTABLE set:
 * an explicit selection, else the lone selectable price (a single-price product
 * has no picker, so its price is always "selected"), else the merchant's default.
 * Returns null only when a multi-price product has no selection and no default —
 * the buyer hasn't made a pricing decision yet.
 *
 * Shared by useSelectedPrice (the resolved price object) and useCheckoutState's
 * `effectiveSelectedPriceId` so the price shown and the variant price-scope can't
 * drift apart.
 */
export function resolveSelectedPrice(selectable = [], selectedPriceId, defaultPriceId) {
  const prices = Array.isArray(selectable) ? selectable : [];
  if (prices.length === 0) return null;
  if (selectedPriceId) {
    const selected = prices.find(p => String(p._id) === String(selectedPriceId));
    if (selected) return selected;
  }
  if (prices.length === 1) return prices[0];
  if (defaultPriceId) {
    const defaulted = prices.find(p => String(p._id) === String(defaultPriceId));
    if (defaulted) return defaulted;
  }
  return null;
}

/**
 * The price id the checkout opens with. Precedence:
 *  1. Preview override — the dashboard "Preview price" selector.
 *  2. URL preselect — `?<reference>_preselected=1`, gated by the page's
 *     `allowDynamicPreselectedPrice`. Only picks a SELECTABLE price, so a hidden
 *     one needs `show_prices` alongside it.
 *  3. Abandonment recovery — the draft's `productPriceId`, but ONLY if it's
 *     currently selectable (visible, or revealed via the query). A hidden
 *     recovered price is never resurrected from a recovery link; we open on the
 *     first visible price instead.
 *  4. Merchant default (when enabled).
 *  5. None — multi-price products open unselected so the buyer makes a choice.
 *
 * The URL preselect is resolved HERE, at initial-state time, rather than in
 * PricePicker: switching billing type re-keys Stripe Elements and remounts the
 * picker, so a mount effect would re-apply the still-present query param and
 * snap the buyer's new choice back, making the other billing type unreachable.
 *
 * Single-price products resolve their lone price downstream regardless, so a
 * null here is fine.
 */
export function getInitialSelectedPriceId({
  product,
  query = {},
  draftCheckout = null,
  isPreview = false,
  previewSelectedPriceId = null,
  allowDynamicPreselectedPrice = false,
} = {}) {
  const prices = product?.prices;
  if (!Array.isArray(prices) || prices.length === 0) return null;

  const enabledPrices = prices.filter(p => p.enabled !== false);
  const selectable = selectablePrices(prices, query);

  if (isPreview && previewSelectedPriceId) {
    const previewPrice = enabledPrices.find(p => String(p._id) === String(previewSelectedPriceId));
    if (previewPrice) return String(previewPrice._id);
  }

  if (allowDynamicPreselectedPrice) {
    const preselected = selectable.find(p => p.reference && query[`${p.reference}_preselected`]);
    if (preselected) return String(preselected._id);
  }

  if (draftCheckout?.productPriceId) {
    const recovered = selectable.find(p => String(p._id) === String(draftCheckout.productPriceId));
    if (recovered) return String(recovered._id);
    const firstVisible = [...selectable].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
    if (firstVisible) return String(firstVisible._id);
  }

  if (product.defaultPriceId) {
    const defaultPrice = enabledPrices.find(p => String(p._id) === String(product.defaultPriceId));
    if (defaultPrice) return String(defaultPrice._id);
  }

  return null;
}
